/** Reads `code` from Firebase Auth errors (works whether or not `instanceof Error`). */
export function getFirebaseAuthErrorCode(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') return undefined;
    const code = 'code' in error ? (error as { code: unknown }).code : undefined;
    return typeof code === 'string' ? code : undefined;
}

const FIREBASE_AUTH_MESSAGES: Record<string, string> = {
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/user-disabled': 'This account has been disabled. Contact your administrator.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Invalid email or password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please wait a moment and try again.',
    'auth/network-request-failed': 'Network error. Check your connection and try again.',
    'auth/requires-recent-login': 'This action requires a recent login. Please sign out and sign back in.',
    'auth/popup-closed-by-user': 'Sign-in was canceled.',
};

const HTTP_STATUS_MESSAGES: Record<number, string> = {
    400: 'The request was invalid. Please check your input.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'An internal server error occurred. Please try again later.',
    502: 'The server is temporarily unavailable. Please try again later.',
    503: 'The service is temporarily unavailable. Please try again later.',
};

/**
 * Returns a user-friendly error message from a Firebase Auth error,
 * HTTP status code, or generic Error. Logs the raw error for debugging.
 */
export function getUserFriendlyMessage(error: unknown): string {
    if (!error) return 'An unexpected error occurred.';

    const authCode = getFirebaseAuthErrorCode(error);
    if (authCode) {
        const friendly = FIREBASE_AUTH_MESSAGES[authCode];
        if (friendly) return friendly;
    }

    if (error instanceof Error) {
        const statusMatch = error.message.match(/\((\d{3})\)/);
        if (statusMatch) {
            const status = parseInt(statusMatch[1], 10);
            const friendly = HTTP_STATUS_MESSAGES[status];
            if (friendly) return friendly;
        }
    }

    return 'Something went wrong. Please try again.';
}

/**
 * Maps a known HTTP status code to a user-friendly string.
 */
export function getHttpErrorMessage(status: number): string {
    return HTTP_STATUS_MESSAGES[status] || `Request failed (${status}). Please try again.`;
}
